/**
 * IncassoEmailsSection
 *
 * Helpdesk-style two-column email UI for an incasso dossier:
 * left column = email-verloop table, right column = selected-email detail
 * panel with a "Reageer" button. Both are returned as siblings — the parent
 * places them in a 2-column grid like the helpdesk detail page.
 *
 * - Fetches emails via GET /email/incasso-ticket/:id/emails
 * - Marks unread received emails as read on first load
 * - Replying delegates to the parent (`onReply`) which routes to /mass-mail
 *   with the full email so the existing reply-with-quote flow kicks in.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import {
  Spinner,
  Button,
  Chip,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@heroui/react';
import {
  Envelope,
  Paperclip,
  X,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  ArrowBendUpLeft,
} from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { TranslatePreview } from 'shared/components/TranslatePreview';
import { IncassoTicket } from '../shared/incassoTypes';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IncassoEmailAttachment {
  id?: string;
  name: string;
  size: number;
  contentType: string;
  sharePointFileId?: string;
  sharePointFileUrl?: string;
}

export interface IncassoEmail {
  id: number;
  messageId: string;
  subject: string;
  body: string;
  from: string;
  receivedDateTime: string;
  direction: 'sent' | 'received';
  isRead: boolean;
  hasAttachments?: boolean;
  attachments?: IncassoEmailAttachment[];
  metadata?: { to?: string };
}

interface IncassoEmailsSectionProps {
  ticket: IncassoTicket;
  onNewEmail: () => void;
  onReply: (email: IncassoEmail) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const IncassoEmailsSection: React.FC<IncassoEmailsSectionProps> = ({
  ticket,
  onNewEmail,
  onReply,
}) => {
  const { session } = useSession();
  const [emails, setEmails] = useState<IncassoEmail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<IncassoEmail | null>(null);

  const isRecentIncoming = (email: IncassoEmail) => {
    const diffHours =
      (Date.now() - new Date(email.receivedDateTime).getTime()) / (1000 * 60 * 60);
    return email.direction === 'received' && diffHours <= 24;
  };

  const fetchEmails = useCallback(async () => {
    if (!ticket?.id) return;
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/email/incasso-ticket/${Number(ticket.id)}/emails`,
        { method: 'GET' },
        session,
      );
      if (!response.ok) throw new Error(`Fout bij ophalen emails (${response.status})`);
      const data: IncassoEmail[] = await response.json();
      setEmails(data);

      // Auto-select most recent incoming email so the user lands on the
      // thing they almost always want to read first.
      const recentIncoming = data
        .filter((e) => e.direction === 'received')
        .sort(
          (a, b) =>
            new Date(b.receivedDateTime).getTime() -
            new Date(a.receivedDateTime).getTime(),
        )[0];
      if (recentIncoming) setSelectedEmail(recentIncoming);

      // Mark unread received emails as read in the background — same pattern
      // as the helpdesk EmailsSection.
      const hasUnread = data.some((e) => !e.isRead && e.direction === 'received');
      if (hasUnread) {
        fetchWithAuth(
          `${API_URL}/email/incasso-ticket/${Number(ticket.id)}/emails/mark-read`,
          { method: 'PUT' },
          session,
        ).catch((err) =>
          console.error('[IncassoEmailsSection] Error marking emails read:', err),
        );
      }
    } catch (error) {
      console.error('[IncassoEmailsSection] Error fetching emails:', error);
      toast.error('Fout bij laden van emails');
    } finally {
      setIsLoading(false);
    }
  }, [ticket?.id, session]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  if (isLoading) {
    return (
      <>
        <div className="bg-muted border border-muted-foreground/20 rounded-lg flex flex-col items-center justify-center py-12 col-span-full lg:col-span-1">
          <Spinner size="lg" color="primary" />
          <p className="mt-4 text-neutral-secondary-mid">Emails laden...</p>
        </div>
        <div className="bg-muted border border-muted-foreground/20 rounded-lg" />
      </>
    );
  }

  if (emails.length === 0) {
    return (
      <>
        <div className="bg-muted border border-muted-foreground/20 rounded-lg flex flex-col items-center justify-center py-12 p-4 col-span-full lg:col-span-1">
          <Envelope className="h-16 w-16 text-neutral-secondary-mid/50 mb-4" />
          <p className="text-lg font-medium text-neutral-primary mb-2">
            Geen emails gevonden
          </p>
          <p className="text-sm text-neutral-secondary-mid mb-6">
            Er zijn nog geen emails gekoppeld aan dit dossier (INC-{Number(ticket.id)}).
          </p>
          <Button
            onClick={onNewEmail}
            className="bg-brick-primary text-white hover:bg-brick-primary/90"
            startContent={<div className="h-4 w-4 text-white">+</div>}
          >
            Verstuur eerste email
          </Button>
        </div>
        <div className="bg-muted border border-muted-foreground/20 rounded-lg" />
      </>
    );
  }

  return (
    <>
      {/* Email Flow Table - Left Column */}
      <div className="bg-muted border border-muted-foreground/20 rounded-lg">
        <div className="border-b border-neutral-primary/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Envelope size={20} className="text-moss-primary" />
                <h3 className="font-semibold text-neutral-primary">
                  Email verloop ({emails.length})
                </h3>
              </div>
              <p className="text-xs text-neutral-secondary-mid mt-1">
                Klik op een rij om details te bekijken
              </p>
            </div>
            <Button
              onPress={onNewEmail}
              className="bg-moss-primary text-white hover:bg-moss-primary/90"
              startContent={<div className="h-4 w-4 text-white">+</div>}
              size="sm"
            >
              Verstuur email
            </Button>
          </div>
        </div>
        <div className="p-4">
          <Table
            aria-label="Email verloop tabel"
            classNames={{
              wrapper: 'shadow-none rounded-sm border border-muted-foreground/20 bg-muted',
              th: 'text-foreground font-medium bg-muted-foreground/10',
              td: 'py-3',
            }}
          >
            <TableHeader>
              <TableColumn>RICHTING</TableColumn>
              <TableColumn>DATUM/TIJD</TableColumn>
              <TableColumn>VAN</TableColumn>
              <TableColumn>NAAR</TableColumn>
              <TableColumn>ONDERWERP</TableColumn>
              <TableColumn>STATUS</TableColumn>
              <TableColumn>BIJLAGEN</TableColumn>
            </TableHeader>
            <TableBody>
              {[...emails]
                .sort(
                  (a, b) =>
                    new Date(b.receivedDateTime).getTime() -
                    new Date(a.receivedDateTime).getTime(),
                )
                .map((email) => {
                  const isSent = email.direction === 'sent';
                  const isRecent = isRecentIncoming(email);
                  return (
                    <TableRow
                      key={email.id}
                      className={`hover:bg-neutral-primary/5 cursor-pointer group ${
                        selectedEmail && selectedEmail.id === email.id
                          ? 'bg-moss-primary/10 border-l-4 border-moss-primary'
                          : ''
                      }`}
                      onClick={() => setSelectedEmail(email)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isSent ? (
                            <ArrowRight className="text-moss-primary" size={16} />
                          ) : (
                            <ArrowLeft className="text-blue-600" size={16} />
                          )}
                          <Chip
                            size="sm"
                            className={
                              isSent
                                ? 'bg-moss-primary/10 text-moss-primary'
                                : 'bg-blue-100 text-blue-700'
                            }
                          >
                            {isSent ? 'Verzonden' : 'Ontvangen'}
                          </Chip>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">
                            {new Date(email.receivedDateTime).toLocaleDateString(
                              'nl-NL',
                            )}
                          </div>
                          <div className="text-neutral-secondary-mid">
                            {new Date(email.receivedDateTime).toLocaleTimeString(
                              'nl-NL',
                              { hour: '2-digit', minute: '2-digit' },
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium truncate max-w-32">
                          {email.from}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium truncate max-w-32">
                          {email.metadata?.to || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium truncate max-w-48">
                          {email.subject}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {!email.isRead && !isSent && (
                            <Chip size="sm" className="bg-red-100 text-red-700">
                              Ongelezen
                            </Chip>
                          )}
                          {isRecent && (
                            <Chip size="sm" className="bg-orange-100 text-orange-700">
                              Nieuw
                            </Chip>
                          )}
                          {email.isRead && !isSent && (
                            <Chip size="sm" className="bg-green-100 text-green-700">
                              Gelezen
                            </Chip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {email.hasAttachments && (
                          <div className="flex items-center gap-1">
                            <Paperclip
                              size={14}
                              className="text-neutral-secondary-mid"
                            />
                            <span className="text-sm text-neutral-secondary-mid">
                              {email.attachments?.length ?? '?'}
                            </span>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Expanded Email View - Right Column */}
      <div className="bg-muted border border-muted-foreground/20 rounded-lg flex flex-col">
        <div className="sticky top-0 z-10 bg-muted border-b border-neutral-primary/10 px-4 py-3 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Envelope size={20} className="text-moss-primary" />
              <h3 className="font-semibold text-neutral-primary">Email Details</h3>
            </div>
            <div className="flex items-center gap-2">
              {selectedEmail && selectedEmail.direction === 'received' && (
                <Button
                  onClick={() => onReply(selectedEmail)}
                  size="sm"
                  className="bg-brick-secondary-fine text-brick-primary hover:bg-brick-secondary-fine/80"
                  startContent={<ArrowBendUpLeft size={16} weight="bold" />}
                >
                  Reageer
                </Button>
              )}
              {selectedEmail && (
                <Button
                  onClick={() => setSelectedEmail(null)}
                  isIconOnly
                  variant="light"
                  className="text-neutral-secondary-mid hover:text-brick-primary"
                  size="sm"
                >
                  <X size={18} />
                </Button>
              )}
            </div>
          </div>
        </div>
        {selectedEmail ? (
          <div className="p-6">
            {/* Email Header */}
            <div className="mb-4 pb-4 border-b border-neutral-primary/10">
              <h4 className="text-lg font-semibold text-neutral-primary mb-2">
                {selectedEmail.subject}
              </h4>
              <div className="text-sm text-neutral-secondary-mid space-y-1">
                <div>
                  <strong>Van:</strong> {selectedEmail.from}
                </div>
                {selectedEmail.metadata?.to && (
                  <div>
                    <strong>Naar:</strong> {selectedEmail.metadata.to}
                  </div>
                )}
                <div>
                  <strong>Datum:</strong>{' '}
                  {new Date(selectedEmail.receivedDateTime).toLocaleString('nl-NL')}
                </div>
                <div className="flex items-center gap-2">
                  <strong>Richting:</strong>
                  <Chip
                    size="sm"
                    className={
                      selectedEmail.direction === 'sent'
                        ? 'bg-moss-primary/10 text-moss-primary'
                        : 'bg-blue-100 text-blue-700'
                    }
                  >
                    {selectedEmail.direction === 'sent' ? 'Verzonden' : 'Ontvangen'}
                  </Chip>
                </div>
              </div>
            </div>

            {/* Email Body */}
            <div className="mb-6">
              <h5 className="font-medium text-neutral-primary mb-3">Bericht</h5>
              <TranslatePreview
                htmlContent={selectedEmail.body}
                bodyClassName="prose prose-sm max-w-none text-neutral-primary bg-white p-4 rounded-sm border max-h-96 overflow-y-auto"
              />
            </div>

            {/* Attachments — link out to SharePoint when we have a URL,
                otherwise just list filenames. We don't replicate the helpdesk
                AttachmentViewer endpoints here because incasso uses a separate
                attachment surface. */}
            {selectedEmail.hasAttachments &&
              selectedEmail.attachments &&
              selectedEmail.attachments.length > 0 && (
                <div>
                  <h5 className="font-medium text-neutral-primary text-sm mb-2">
                    Bijlagen ({selectedEmail.attachments.length})
                  </h5>
                  <div className="space-y-2">
                    {selectedEmail.attachments.map((attachment, index) => {
                      const content = (
                        <>
                          <Paperclip
                            size={14}
                            className="text-neutral-secondary-mid"
                          />
                          <span className="truncate">{attachment.name}</span>
                          {typeof attachment.size === 'number' && (
                            <span className="text-xs text-neutral-secondary-mid">
                              ({Math.round(attachment.size / 1024)} kB)
                            </span>
                          )}
                          {attachment.sharePointFileUrl && (
                            <CheckCircle
                              size={14}
                              className="text-moss-primary ml-auto"
                              weight="fill"
                            />
                          )}
                        </>
                      );
                      const className =
                        'flex items-center gap-2 rounded-md border border-neutral-primary/10 bg-white px-3 py-2 text-sm';
                      return attachment.sharePointFileUrl ? (
                        <a
                          key={`${attachment.name}-${index}`}
                          href={attachment.sharePointFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${className} hover:bg-moss-primary/5 hover:border-moss-primary/30`}
                        >
                          {content}
                        </a>
                      ) : (
                        <div
                          key={`${attachment.name}-${index}`}
                          className={className}
                        >
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 p-4">
            <Envelope className="h-16 w-16 text-neutral-secondary-mid/50 mb-4" />
            <p className="text-sm text-neutral-secondary-mid text-center">
              Selecteer een email uit de lijst om details te bekijken
            </p>
          </div>
        )}
      </div>
    </>
  );
};
